import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForMedicalDoctorsPageFr() {
  return (
    <>
      <Head>
        <title>
          Featcal pour Médecins | Suivi structuré, meilleure adhérence
        </title>
        <meta
          name="description"
          content="Featcal aide les médecins à structurer le suivi entre visites : données déclaratives patient, synthèses prêtes à la revue et meilleure adhérence-sans charge administrative."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Pour médecins
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Une vision plus claire entre visites-sans courir après la donnée.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal collecte le suivi déclaratif patient et le transforme en
            flow de revue structuré pour intervenir plus tôt et améliorer
            l’adhérence-perte de poids, prise de poids et santé métabolique.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Demander une démo
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              Voir la FAQ
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Le vrai sujet : le suivi entre les visites
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "La donnée déclarative patient est dispersée entre apps et messages.",
                "Difficile de repérer tôt les problèmes d’adhérence.",
                "Le temps équipe part en suivi opérationnel et questions de routine.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Suivi déclaratif structuré",
                desc: "Poids, repas, activité, sommeil, hydratation… adapté à votre parcours clinique.",
              },
              {
                title: "Synthèses prêtes à la revue",
                desc: "Tendances et gaps d’adhérence avant la prochaine consultation-sans nettoyage manuel.",
              },
              {
                title: "Moins de bruit opérationnel",
                desc: "Nudges et prochaines étapes plus claires : moins de relances et de questions de routine.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Idéal pour
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Parcours obésité et santé métabolique avec suivi structuré.",
                  "Programmes médecine du mode de vie centrés sur l’adhérence.",
                  "Monitoring post-intervention après ajustement thérapeutique.",
                  "Modèles hybrides où le suivi entre visites est essentiel.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Ce que Featcal remplace
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Formulaires, apps et messages dispersés.",
                  "Synthèses manuelles avant consultation.",
                  "Appels ad-hoc pour des sujets de routine.",
                  "Bruit opérationnel qui ralentit l’équipe.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Dashboard clinicien"
          title="Un snapshot clair avant chaque consult"
          description="Relisez tendances et adhérence rapidement via un dashboard structuré, pour un suivi cohérent sans surcouche d’admin."
          bullets={[
            "Revue plus rapide avant consultation.",
            "Suivi plus structuré sur l’ensemble des patients.",
            "Moins d’allers-retours manuels pour l’équipe.",
          ]}
          imageSrc="/images/customer-admin.webp"
          imageAlt="Aperçu du dashboard clinicien"
          align="left"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Prêt à réduire le bruit opérationnel du suivi ?
                </h2>
                <p className="text-slate-700 mt-2">
                  Demandez une démo pour voir Featcal dans votre workflow.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Demander une démo
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
